import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, UserCheck, X, Award, ExternalLink, Image, ChevronDown, Shield, Clock, AlertTriangle, Bell, Smartphone, RefreshCw, Save, Users, Search, MessageSquare, CreditCard, Globe, Flag, Send } from 'lucide-react';
import { AppUser, TesterProfile, TesterRequest, TesterThread, TesterPromotion, CampaignSentEntry } from '../../../types';
import { fetchTesterPromotions, approveTesterPromotion, sendPushToTesters } from '../../../services/testerPromotionService';
import { fetchCampaignSentForUser, fetchCampaignNameMaps } from '../../../services/campaignService';
import { sendTesterMessage } from '../../../services/firebaseService';

const COUNTRY_INFO: Record<string, { key: string; name: string; flag: string }> = {
  sweden: { key: 'sweden', name: 'Sverige', flag: '🇸🇪' },
  sverige: { key: 'sweden', name: 'Sverige', flag: '🇸🇪' },
  finland: { key: 'finland', name: 'Finland', flag: '🇫🇮' },
  finsk: { key: 'finland', name: 'Finland', flag: '🇫🇮' },
  denmark: { key: 'denmark', name: 'Danmark', flag: '🇩🇰' },
  danmark: { key: 'denmark', name: 'Danmark', flag: '🇩🇰' },
  dansk: { key: 'denmark', name: 'Danmark', flag: '🇩🇰' },
  iceland: { key: 'iceland', name: 'Island', flag: '🇮🇸' },
  island: { key: 'iceland', name: 'Island', flag: '🇮🇸' },
};

export function getCountryRequestsForUser(
  user: { requestedCountries?: string[] },
  thread?: TesterThread,
  request?: TesterRequest
): { key: string; name: string; flag: string }[] {
  const keys = new Set<string>();
  if (Array.isArray(user?.requestedCountries)) {
    user.requestedCountries.forEach(c => {
      const match = COUNTRY_INFO[String(c).toLowerCase()];
      if (match) keys.add(match.key);
    });
  }
  const text = [thread?.lastMessage || '', request?.motivation || ''].join(' ').toLowerCase();
  if (text.includes('sverige') || text.includes('sweden')) keys.add('sweden');
  if (text.includes('finland') || text.includes('finsk')) keys.add('finland');
  if (text.includes('danmark') || text.includes('denmark') || text.includes('dansk')) keys.add('denmark');
  if (text.includes('island') || text.includes('iceland')) keys.add('iceland');

  const result: { key: string; name: string; flag: string }[] = [];
  keys.forEach(k => {
    if (COUNTRY_INFO[k]) result.push(COUNTRY_INFO[k]);
  });
  return result;
}

type AdminTestersPanelProps = {
  currentUser?: AppUser | null;
  testerRequests: TesterRequest[];
  approvedTesters: TesterProfile[];
  sortedApprovedTesters: TesterProfile[];
  testerThreadByUid: Map<string, TesterThread>;
  isMobileViewport: boolean;
  processingId: string | null;
  testerEnabled: boolean;
  onToggleTesterApps: (enabled: boolean) => Promise<void>;
  onOpenTesterInbox: (tester: TesterProfile) => void;
  onEndTesterByAdmin: (tester: TesterProfile) => void;
  onResolveTesterRequest: (request: TesterRequest, status: 'approved' | 'rejected', reason?: string, validDays?: number) => void;
  testflightSyncEnabled: boolean;
  testflightBetaGroupId: string;
  testflightPublic: boolean;
  testflightSyncStatus: { last_sync_at?: any; last_sync_result?: { added: number; removed: number; errors: number; duration_ms: number } } | null;
  onToggleTestFlightSync: (enabled: boolean) => Promise<void>;
  onToggleTestFlightPublic: (pub: boolean) => Promise<void>;
  onSaveTestFlightConfig: (betaGroupId: string) => Promise<void>;
  onRunTestFlightSync: () => Promise<void>;
  allUsers: AppUser[];
  onOpenUserInbox: (user: AppUser) => void;
  onPromoteToTester?: (user: AppUser, validDays?: number) => void;
};

const AdminTestersPanel: React.FC<AdminTestersPanelProps> = ({
  currentUser,
  testerRequests,
  approvedTesters,
  sortedApprovedTesters,
  testerThreadByUid,
  isMobileViewport,
  processingId,
  testerEnabled,
  onToggleTesterApps,
  onOpenTesterInbox,
  onEndTesterByAdmin,
  onResolveTesterRequest,
  testflightSyncEnabled,
  testflightBetaGroupId,
  testflightPublic,
  testflightSyncStatus,
  onToggleTestFlightSync,
  onToggleTestFlightPublic,
  onSaveTestFlightConfig,
  onRunTestFlightSync,
  allUsers,
  onOpenUserInbox,
  onPromoteToTester
}) => {
  const { t } = useTranslation();
  const [promotions, setPromotions] = useState<TesterPromotion[]>([]);
  const [promosLoading, setPromosLoading] = useState(false);
  const [promoProcessingId, setPromoProcessingId] = useState<string | null>(null);
  const [savedToast, setSavedToast] = useState(false);
  const [expandedTesters, setExpandedTesters] = useState<Set<string>>(new Set());
  const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectingPromoId, setRejectingPromoId] = useState<string | null>(null);
  const [promoRejectReason, setPromoRejectReason] = useState('');
  const [promotingUserId, setPromotingUserId] = useState<string | null>(null);
  const [promoteDays, setPromoteDays] = useState(90);
  const [approvingRequestId, setApprovingRequestId] = useState<string | null>(null);
  const [approveDays, setApproveDays] = useState(30);
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushSending, setPushSending] = useState(false);
  const [pushResult, setPushResult] = useState<{ sent: number; total: number } | null>(null);
  const [showPushForm, setShowPushForm] = useState(false);
  const [showTestFlight, setShowTestFlight] = useState(false);
  const [localBetaGroupId, setLocalBetaGroupId] = useState(testflightBetaGroupId);
  const [testflightSaving, setTestflightSaving] = useState(false);
  const [testflightSyncing, setTestflightSyncing] = useState(false);
  const [testflightSavedToast, setTestflightSavedToast] = useState(false);

  const [activeSubTab, setActiveSubTab] = useState<'testers' | 'users'>('testers');
  const [userSearch, setUserSearch] = useState('');
  const [showPaidOnly, setShowPaidOnly] = useState(false);
  const [countryFilter, setCountryFilter] = useState<'all' | 'requested_any' | 'sweden' | 'finland' | 'denmark' | 'iceland'>('all');
  const [sendWelcomeMsg, setSendWelcomeMsg] = useState(true);
  const [customWelcomeMsg, setCustomWelcomeMsg] = useState('');

  // Campaign email history per tester (lazy-loaded on expand)
  const [campaignSentByUid, setCampaignSentByUid] = useState<Map<string, CampaignSentEntry[]>>(new Map());
  const [campaignNamesLoaded, setCampaignNamesLoaded] = useState(false);
  const [templateNames, setTemplateNames] = useState<Map<string, string>>(new Map());
  const [loadingCampaignUid, setLoadingCampaignUid] = useState<string | null>(null);

  useEffect(() => { setLocalBetaGroupId(testflightBetaGroupId); }, [testflightBetaGroupId]);

  const handleSaveTestFlightConfig = async () => {
    setTestflightSaving(true);
    try {
      await onSaveTestFlightConfig(localBetaGroupId);
      setTestflightSavedToast(true);
      setTimeout(() => setTestflightSavedToast(false), 3000);
    } catch { /* error in parent */ }
    finally { setTestflightSaving(false); }
  };

  const handleRunSync = async () => {
    setTestflightSyncing(true);
    try { await onRunTestFlightSync(); }
    catch { /* error in parent */ }
    finally { setTestflightSyncing(false); }
  };

  const testflightOptInTesters = approvedTesters.filter(t => t.testflight_opt_in);

  const testerUids = new Set(approvedTesters.map(t => t.uid));
  const nonTesterUsers = allUsers.filter(u => !testerUids.has(u.uid));
  const paidCount = nonTesterUsers.filter(u => u.subscriptionStatus === 'paid').length;

  const usersWithCountryRequestsCount = nonTesterUsers.filter(u =>
    getCountryRequestsForUser(u, testerThreadByUid.get(u.uid)).length > 0
  ).length;

  const swedenCount = nonTesterUsers.filter(u =>
    getCountryRequestsForUser(u, testerThreadByUid.get(u.uid)).some(c => c.key === 'sweden')
  ).length;
  const finlandCount = nonTesterUsers.filter(u =>
    getCountryRequestsForUser(u, testerThreadByUid.get(u.uid)).some(c => c.key === 'finland')
  ).length;
  const denmarkCount = nonTesterUsers.filter(u =>
    getCountryRequestsForUser(u, testerThreadByUid.get(u.uid)).some(c => c.key === 'denmark')
  ).length;
  const icelandCount = nonTesterUsers.filter(u =>
    getCountryRequestsForUser(u, testerThreadByUid.get(u.uid)).some(c => c.key === 'iceland')
  ).length;

  const filteredUsers = nonTesterUsers.filter(u => {
    if (showPaidOnly && u.subscriptionStatus !== 'paid') return false;
    const reqs = getCountryRequestsForUser(u, testerThreadByUid.get(u.uid));
    if (countryFilter === 'requested_any' && reqs.length === 0) return false;
    if (countryFilter !== 'all' && countryFilter !== 'requested_any') {
      if (!reqs.some(c => c.key === countryFilter)) return false;
    }
    if (userSearch.trim()) {
      const q = userSearch.toLowerCase();
      return (u.displayName || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
    }
    return true;
  });

  const threadSortValue = (createdAt: any): number => {
    if (!createdAt) return 0;
    if (typeof createdAt === 'number') return createdAt;
    if (typeof createdAt?.toMillis === 'function') return createdAt.toMillis();
    if (typeof createdAt?.seconds === 'number') return createdAt.seconds * 1000;
    return 0;
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const threadA = testerThreadByUid.get(a.uid);
    const threadB = testerThreadByUid.get(b.uid);
    const reqsA = getCountryRequestsForUser(a, threadA).length;
    const reqsB = getCountryRequestsForUser(b, threadB).length;

    if (reqsA !== reqsB) return reqsB - reqsA;

    const unreadA = (threadA?.unreadForAdmin || 0) > 0 ? 1 : 0;
    const unreadB = (threadB?.unreadForAdmin || 0) > 0 ? 1 : 0;
    if (unreadA !== unreadB) return unreadB - unreadA;

    const timeA = threadSortValue(threadA?.lastMessageAt);
    const timeB = threadSortValue(threadB?.lastMessageAt);
    if (timeA !== timeB) return timeB - timeA;

    return (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '', 'nb');
  });

  const buildDefaultWelcomeMessage = (userName: string, reqs: { key: string; name: string; flag: string }[]) => {
    const countryListStr = reqs.length > 0
      ? reqs.map(c => `${c.flag} ${c.name}`).join(' og ')
      : '🇸🇪 Sverige, 🇩🇰 Danmark og 🇫🇮 Finland';
    return `Hei ${userName || ''}! Du har nå fått testerstatus i Fricamp! 🏕️\n\nDu har fått tilgang til kartet og alle plasser i ${countryListStr}. Vi vil veldig gjerne ha din tilbakemelding på hvordan du opplever resultatene og funksjonene i kartet!\n\nGod tur, og gi oss beskjed her i chatten hvis du oppdager noe eller har innspill.`;
  };

  const toggleExpand = async (uid: string) => {
    const willExpand = !expandedTesters.has(uid);
    setExpandedTesters(prev => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      return next;
    });

    // Lazy-load campaign emails on first expand
    if (willExpand && !campaignSentByUid.has(uid)) {
      setLoadingCampaignUid(uid);
      try {
        if (!campaignNamesLoaded) {
          const maps = await fetchCampaignNameMaps();
          setTemplateNames(maps.templates);
          setCampaignNamesLoaded(true);
        }
        const sent = await fetchCampaignSentForUser(uid);
        setCampaignSentByUid(prev => new Map(prev).set(uid, sent));
      } catch (e) {
        console.warn('Failed to load campaign history for', uid, e);
        setCampaignSentByUid(prev => new Map(prev).set(uid, []));
      } finally {
        setLoadingCampaignUid(null);
      }
    }
  };

  const getTesterStatus = (tester: TesterProfile) => {
    const feedbackCount = tester.feedback_count || 0;
    const promoCount = tester.social_promo_count || 0;
    const hasMetRequirements = feedbackCount > 0 || promoCount >= 5;

    let expiryTimeMs: number | null = null;
    if (tester.tester_valid_until) {
      expiryTimeMs = tester.tester_valid_until.toMillis
        ? tester.tester_valid_until.toMillis()
        : (tester.tester_valid_until.seconds ? tester.tester_valid_until.seconds * 1000 : null);
    } else if (tester.tester_joined_at) {
      const joinedMs = tester.tester_joined_at.toMillis
        ? tester.tester_joined_at.toMillis()
        : (tester.tester_joined_at.seconds ? tester.tester_joined_at.seconds * 1000 : null);
      if (joinedMs) {
        expiryTimeMs = joinedMs + 30 * 24 * 60 * 60 * 1000;
      }
    }

    let daysRemaining: number | null = null;
    if (expiryTimeMs && !hasMetRequirements) {
      daysRemaining = Math.max(0, Math.ceil((expiryTimeMs - Date.now()) / (24 * 60 * 60 * 1000)));
    }

    const isExpired = daysRemaining !== null && daysRemaining <= 0;
    const isAmbassador = !!tester.tester_valid_until && (hasMetRequirements || daysRemaining === null);
    return { feedbackCount, promoCount, isAmbassador, hasMetRequirements, daysRemaining, isExpired };
  };

  const handleToggle = async () => {
    try {
      await onToggleTesterApps(!testerEnabled);
      setSavedToast(true);
      setTimeout(() => setSavedToast(false), 3000);
    } catch { /* error handled in parent */ }
  };

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    setPromosLoading(true);
    try {
      const res = await fetchTesterPromotions('pending');
      setPromotions(res.promotions);
    } catch { /* silent */ }
    finally { setPromosLoading(false); }
  };

  const handlePromoAction = async (promoId: string, action: 'approved' | 'rejected', reason?: string) => {
    setPromoProcessingId(promoId);
    try {
      await approveTesterPromotion({ promotion_id: promoId, action, reason: reason || undefined });
      setPromotions(prev => prev.filter(p => p.id !== promoId));
      setRejectingPromoId(null);
      setPromoRejectReason('');
    } catch { /* silent */ }
    finally { setPromoProcessingId(null); }
  };

  const handleSendPush = async () => {
    if (!pushTitle.trim() || !pushBody.trim()) return;
    setPushSending(true);
    setPushResult(null);
    try {
      const res = await sendPushToTesters({ title: pushTitle.trim(), body: pushBody.trim() });
      setPushResult({ sent: res.sent, total: res.total });
      setPushTitle('');
      setPushBody('');
    } catch { /* silent */ }
    finally { setPushSending(false); }
  };

  const testflightSection = (
    <div className="rounded-2xl border border-cyan-500/20 bg-cyan-500/5 overflow-hidden">
      <button
        onClick={() => setShowTestFlight(!showTestFlight)}
        className="w-full p-4 flex items-center justify-between hover:bg-[var(--bg-surface-alt)] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-cyan-500/10 rounded-xl text-cyan-400 border border-cyan-500/20">
            <Smartphone size={16} />
          </div>
          <div className="text-left">
            <p className="text-2xs font-black uppercase tracking-widest text-cyan-300">
              {t('admin.testflight_section_title', 'TestFlight-synkronisering')}
            </p>
            <p className="text-2xs text-[var(--text-muted)]">
              {t('admin.testflight_section_desc', 'Synkroniser testere til App Store Connect TestFlight.')}
              {testflightOptInTesters.length > 0 && (
                <span className="text-cyan-400 ml-1">({testflightOptInTesters.length} {t('admin.testflight_opted_in', 'påmeldt')})</span>
              )}
            </p>
          </div>
        </div>
        <ChevronDown size={16} className={`text-[var(--text-muted)] transition-transform ${showTestFlight ? 'rotate-180' : ''}`} />
      </button>
      {showTestFlight && (
        <div className="px-4 pb-4 space-y-4 border-t border-[var(--border-primary)]">
          {/* Enable/disable toggle */}
          <div className="flex items-center justify-between mt-3">
            <span className="text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)]">
              {t('admin.testflight_enabled', 'TestFlight-synk aktiv')}
            </span>
            <button
              onClick={() => onToggleTestFlightSync(!testflightSyncEnabled)}
              disabled={processingId === 'testflight-toggle'}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${testflightSyncEnabled ? 'bg-cyan-600' : 'bg-[var(--bg-input)]'} ${processingId === 'testflight-toggle' ? 'opacity-60' : ''}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${testflightSyncEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Public visibility toggle */}
          <div className="flex items-center justify-between">
            <span className="text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)]">
              {t('admin.testflight_public', 'Synlig for alle brukere')}
            </span>
            <button
              onClick={() => onToggleTestFlightPublic(!testflightPublic)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${testflightPublic ? 'bg-emerald-600' : 'bg-[var(--bg-input)]'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${testflightPublic ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>

          {/* Beta Group ID */}
          <div>
            <label className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest block mb-1">
              {t('admin.testflight_beta_group_id', 'Beta Group ID')}
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={localBetaGroupId}
                onChange={e => setLocalBetaGroupId(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                className="flex-1 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-3 py-2 text-xs text-[var(--text-secondary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-cyan-500/40 font-mono"
              />
              <button
                onClick={handleSaveTestFlightConfig}
                disabled={testflightSaving || localBetaGroupId === testflightBetaGroupId}
                className="px-3 py-2 rounded-lg bg-cyan-600/20 border border-cyan-500/30 text-cyan-300 text-3xs font-black uppercase tracking-widest hover:bg-cyan-600/30 disabled:opacity-40 flex items-center gap-1.5 transition-all"
              >
                {testflightSaving ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                {t('common.save', 'Lagre')}
              </button>
            </div>
            {testflightSavedToast && (
              <p className="text-3xs text-emerald-400 mt-1">
                <CheckCircle size={9} className="inline mr-1" />
                {t('common.saved', 'Lagret')}
              </p>
            )}
          </div>

          {/* Sync now + status */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleRunSync}
              disabled={testflightSyncing || !testflightSyncEnabled || !testflightBetaGroupId}
              className="px-4 py-2.5 rounded-xl bg-cyan-600/20 hover:bg-cyan-600 border border-cyan-500/30 text-cyan-300 hover:text-white text-2xs font-black uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 disabled:opacity-40"
            >
              {testflightSyncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
              {t('admin.testflight_sync_now', 'Synk nå')}
            </button>
            {testflightSyncStatus?.last_sync_at && (
              <div className="text-right">
                <p className="text-3xs text-[var(--text-muted)]">
                  {t('admin.testflight_last_sync', 'Siste synk')}: {new Date(testflightSyncStatus.last_sync_at?.seconds ? testflightSyncStatus.last_sync_at.seconds * 1000 : testflightSyncStatus.last_sync_at).toLocaleString()}
                </p>
                {testflightSyncStatus.last_sync_result && (
                  <p className="text-3xs text-[var(--text-muted)]">
                    +{testflightSyncStatus.last_sync_result.added} / -{testflightSyncStatus.last_sync_result.removed} / {testflightSyncStatus.last_sync_result.errors} {t('admin.testflight_errors', 'feil')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Opted-in testers list */}
          {testflightOptInTesters.length > 0 && (
            <div>
              <p className="text-3xs font-black text-[var(--text-muted)] uppercase tracking-widest mb-2">
                {t('admin.testflight_opted_in_list', 'Påmeldte testere')} ({testflightOptInTesters.length})
              </p>
              <div className="space-y-1.5">
                {testflightOptInTesters.map(tester => (
                  <div key={tester.uid} className="flex items-center justify-between bg-[var(--bg-input)] rounded-lg px-3 py-2 border border-[var(--border-primary)]">
                    <div className="min-w-0">
                      <p className="text-tiny text-[var(--text-primary)] font-bold truncate">{tester.displayName || tester.uid}</p>
                      <p className="text-3xs text-[var(--text-muted)] truncate">{tester.email}</p>
                    </div>
                    <div className="shrink-0 ml-2">
                      {tester.testflight_synced ? (
                        <span className="text-4xs font-black uppercase tracking-widest text-emerald-400 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30">
                          {t('admin.testflight_status_synced', 'Synket')}
                        </span>
                      ) : tester.testflight_sync_error ? (
                        <span className="text-4xs font-black uppercase tracking-widest text-red-400 px-2 py-0.5 rounded-full bg-red-500/20 border border-red-500/30">
                          {t('admin.testflight_status_error', 'Feil')}
                        </span>
                      ) : (
                        <span className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)] px-2 py-0.5 rounded-full bg-[var(--bg-input)]/50 border border-[var(--border-secondary)]">
                          {t('admin.testflight_status_pending', 'Venter')}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const testerUnread = Array.from(testerThreadByUid.values()).filter(t => (t.unreadForAdmin || 0) > 0 && testerUids.has(t.testerUid || t.id || '')).length;
  const userUnread = sortedUsers.filter(u => (testerThreadByUid.get(u.uid)?.unreadForAdmin || 0) > 0).length;

  return (
    <div className="space-y-4">
      {/* Sub-tab switcher */}
      <div className="flex gap-1 bg-[var(--bg-surface-alt)] rounded-2xl p-1">
        <button
          onClick={() => setActiveSubTab('testers')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'testers' ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
        >
          <UserCheck size={12} />
          {t('admin.subtab_testers', 'Testere')}
          {approvedTesters.length > 0 && <span className={`px-1.5 py-0.5 rounded-full text-4xs font-black ${activeSubTab === 'testers' ? 'bg-[var(--bg-surface)]' : 'bg-[var(--bg-surface)]'}`}>{approvedTesters.length}</span>}
          {testerUnread > 0 && <span className="w-2 h-2 rounded-full bg-red-500" />}
        </button>
        <button
          onClick={() => setActiveSubTab('users')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${activeSubTab === 'users' ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/20' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
        >
          <Users size={12} />
          {t('admin.subtab_users', 'Brukere')}
          {nonTesterUsers.length > 0 && <span className={`px-1.5 py-0.5 rounded-full text-4xs font-black ${activeSubTab === 'users' ? 'bg-[var(--bg-surface)]' : 'bg-[var(--bg-surface)]'}`}>{nonTesterUsers.length}</span>}
          {userUnread > 0 && <span className="w-2 h-2 rounded-full bg-red-500" />}
        </button>
      </div>

      {activeSubTab === 'testers' ? (
        <div className="space-y-4">
          {/* Tester-søknader-bryteren ligger nå kun i headeren (unngå duplikat).
              TestFlight-synk skjules i mobilvisning. */}
          <div className="hidden sm:block">{testflightSection}</div>
          {/* Push broadcast to all testers */}
          {approvedTesters.length > 0 && (
        <div className="rounded-2xl border border-violet-500/20 bg-violet-500/5 overflow-hidden">
          <button
            onClick={() => { setShowPushForm(!showPushForm); setPushResult(null); }}
            className="w-full p-4 flex items-center justify-between hover:bg-[var(--bg-surface-alt)] transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500/10 rounded-xl text-violet-400 border border-violet-500/20">
                <Bell size={16} />
              </div>
              <div className="text-left">
                <p className="text-2xs font-black uppercase tracking-widest text-violet-300">{t('admin.push_testers_title', 'Push til alle testere')}</p>
                <p className="text-2xs text-[var(--text-muted)]">{t('admin.push_testers_desc', 'Send push-varsling til alle {{count}} testere på tvers av plattformer.', { count: approvedTesters.length })}</p>
              </div>
            </div>
            <ChevronDown size={16} className={`text-[var(--text-muted)] transition-transform ${showPushForm ? 'rotate-180' : ''}`} />
          </button>
          {showPushForm && (
            <div className="px-4 pb-4 space-y-3 border-t border-[var(--border-primary)]">
              <input
                type="text"
                value={pushTitle}
                onChange={e => setPushTitle(e.target.value)}
                placeholder={t('admin.push_title_placeholder', 'Tittel, f.eks. "Ny funksjon klar for testing!"')}
                className="w-full mt-3 bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg px-3 py-2.5 text-xs text-[var(--text-secondary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-violet-500/40"
              />
              <textarea
                value={pushBody}
                onChange={e => setPushBody(e.target.value)}
                placeholder={t('admin.push_body_placeholder', 'Melding, f.eks. "Vi har lagt til rutefilter – test og gi tilbakemelding!"')}
                className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg p-3 text-xs text-[var(--text-secondary)] placeholder:text-[var(--text-muted)] resize-none focus:outline-none focus:border-violet-500/40"
                rows={2}
              />
              <div className="flex items-center gap-3">
                <button
                  onClick={handleSendPush}
                  disabled={pushSending || !pushTitle.trim() || !pushBody.trim()}
                  className="px-4 py-2.5 rounded-xl bg-violet-600/20 hover:bg-violet-600 border border-violet-500/30 text-violet-300 hover:text-white text-2xs font-black uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 disabled:opacity-40"
                >
                  {pushSending ? <Loader2 className="animate-spin" size={12} /> : <Bell size={12} />}
                  {t('admin.push_send_btn', 'Send push')}
                </button>
                {pushResult && (
                  <span className="text-2xs font-bold text-emerald-400">
                    <CheckCircle size={11} className="inline -mt-0.5 mr-1" />
                    {t('admin.push_result', 'Sendt til {{sent}} av {{total}} testere', { sent: pushResult.sent, total: pushResult.total })}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending tester requests — shown first so they're always visible */}
      {testerRequests.length > 0 && (
        <div className="space-y-3 mb-6">
          <h3 className="text-2xs font-black uppercase tracking-widest text-pink-400">
            {t('admin.pending_requests_count', 'Søknader ({{count}})', { count: testerRequests.length })}
          </h3>
          {testerRequests.map((req) => (
            <div key={req.id} className="bg-pink-500/5 border border-pink-500/20 rounded-2xl p-5 space-y-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-pink-500/10 rounded-xl text-pink-500 border border-pink-500/20">
                    <UserCheck size={20} />
                  </div>
                  <div>
                    <h3 className="text-[var(--text-primary)] font-black text-sm">{req.userName}</h3>
                    <p className="text-xs text-[var(--text-muted)]">{req.userEmail}</p>
                  </div>
                </div>
                <span className="text-3xs font-mono text-[var(--text-muted)]">
                  {req.createdAt?.seconds
                    ? new Date(req.createdAt.seconds * 1000).toLocaleDateString()
                    : t('common.unknown_date', 'Ukjent dato')}
                </span>
              </div>

              <div className="bg-[var(--bg-input)] p-4 rounded-xl border border-[var(--border-primary)]">
                <p className="text-2xs font-black text-[var(--text-muted)] uppercase mb-2">{t('admin.motivation_label', 'Motivasjon')}</p>
                <p className="text-[var(--text-secondary)] text-xs italic leading-relaxed">"{req.motivation}"</p>
              </div>

              {rejectingRequestId === req.id ? (
                <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center gap-2 text-red-400">
                    <AlertTriangle size={14} />
                    <span className="text-2xs font-black uppercase tracking-widest">{t('admin.reject_reason_title', 'Begrunnelse for avslag')}</span>
                  </div>
                  <textarea
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                    placeholder={t('admin.reject_reason_placeholder', 'Valgfritt: Skriv en kort begrunnelse som sendes til søkeren...')}
                    className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg p-3 text-xs text-[var(--text-secondary)] placeholder:text-[var(--text-muted)] resize-none focus:outline-none focus:border-red-500/40"
                    rows={2}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => { onResolveTesterRequest(req, 'rejected', rejectReason); setRejectingRequestId(null); setRejectReason(''); }}
                      disabled={processingId === req.id}
                      className="flex-1 bg-red-600/20 hover:bg-red-600 border border-red-500/30 text-red-400 hover:text-white py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50"
                    >
                      {processingId === req.id ? <Loader2 className="animate-spin" size={12} /> : <X size={12} />}
                      {t('admin.confirm_reject', 'Bekreft avslag')}
                    </button>
                    <button
                      onClick={() => { setRejectingRequestId(null); setRejectReason(''); }}
                      className="px-4 py-2.5 rounded-xl bg-[var(--bg-input)] text-[var(--text-secondary)] text-2xs font-black uppercase tracking-widest hover:bg-[var(--bg-input)] transition-all"
                    >
                      {t('common.cancel', 'Avbryt')}
                    </button>
                  </div>
                </div>
              ) : approvingRequestId === req.id ? (
                <div className="bg-emerald-500/10 border border-emerald-500/30 p-4 rounded-xl space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                  <p className="text-xs font-bold text-[var(--text-primary)]">
                    {t('admin.select_tester_duration', 'Velg varighet for tester-tilgang (dager)')}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[30, 90, 180, 365].map(days => (
                      <button
                        key={days}
                        onClick={() => setApproveDays(days)}
                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${approveDays === days ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20' : 'bg-[var(--bg-input)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-secondary)]'}`}
                      >
                        {days} {t('admin.days_label', 'dager')}{days === 30 ? ` (${t('admin.default_label', 'Standard')})` : ''}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 pt-1">
                    <span className="text-xs text-[var(--text-muted)]">{t('admin.custom_days', 'Eller egendefinert:')}</span>
                    <input
                      type="number"
                      min="1"
                      max="3650"
                      value={approveDays}
                      onChange={(e) => setApproveDays(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-24 px-3 py-1.5 rounded-xl bg-[var(--bg-input)] border border-[var(--border-secondary)] text-xs text-[var(--text-primary)] focus:outline-none focus:border-emerald-500"
                    />
                    <span className="text-xs text-[var(--text-muted)]">{t('admin.days_label', 'dager')}</span>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      onClick={() => { setApprovingRequestId(null); setApproveDays(30); }}
                      className="px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all"
                    >
                      {t('common.cancel', 'Avbryt')}
                    </button>
                    <button
                      onClick={() => {
                        onResolveTesterRequest(req, 'approved', undefined, approveDays);
                        setApprovingRequestId(null);
                        setApproveDays(30);
                      }}
                      disabled={processingId === req.id}
                      className="px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-emerald-600 text-white shadow-lg shadow-emerald-500/20 hover:bg-emerald-500 transition-all flex items-center gap-1.5"
                    >
                      {processingId === req.id ? <Loader2 className="animate-spin" size={12} /> : <UserCheck size={13} />}
                      {t('admin.confirm_promote', 'Godkjenn som tester')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3 pt-1">
                  <button
                    onClick={() => { setApprovingRequestId(req.id); setApproveDays(30); }}
                    disabled={processingId === req.id}
                    className="flex-1 bg-emerald-600/10 hover:bg-emerald-600 border border-emerald-500/20 text-emerald-500 hover:text-white py-3 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95"
                  >
                    {processingId === req.id ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle size={14} />}
                    {t('common.approve', 'Godkjenn')}
                  </button>
                  <button
                    onClick={() => setRejectingRequestId(req.id)}
                    disabled={processingId === req.id}
                    className="flex-1 bg-[var(--bg-input)] hover:bg-red-900/30 text-[var(--text-secondary)] hover:text-red-400 py-3 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95"
                  >
                    <X size={14} />
                    {t('common.reject', 'Avslå')}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Approved testers — sorted: unread messages first, then by last message time */}
      {approvedTesters.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-2xs font-black uppercase tracking-widest text-emerald-500">
            {t('admin.approved_testers_count', 'Godkjente testere ({{count}})', { count: approvedTesters.length })}
          </h3>
          {sortedApprovedTesters.map((tester) => {
            const thread = testerThreadByUid.get(tester.uid);
            const unreadCount = thread?.unreadForAdmin || 0;
            const msgCount = thread?.messageCount || 0;
            const lastMsgMs = threadSortValue(thread?.lastMessageAt);
            const isExpanded = expandedTesters.has(tester.uid);
            const status = getTesterStatus(tester);

            return (
              <div key={tester.uid} className={`border rounded-2xl overflow-hidden transition-all ${unreadCount > 0 ? 'bg-red-500/5 border-red-500/25' : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)]'}`}>
                {/* Main row — click to expand */}
                <div
                  className={`p-4 flex ${isMobileViewport ? 'flex-col gap-3' : 'items-center justify-between'} cursor-pointer hover:bg-[var(--bg-surface)] transition-colors`}
                  onClick={() => toggleExpand(tester.uid)}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-[var(--bg-input)] border border-[var(--border-secondary)] overflow-hidden">
                        {tester.photoURL ? (
                          <img src={tester.photoURL} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[var(--text-muted)]">
                            <UserCheck size={20} />
                          </div>
                        )}
                      </div>
                      {unreadCount > 0 && (
                        <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 border-2 border-[#0a0c0c] flex items-center justify-center">
                          <span className="text-4xs font-black text-white">{unreadCount}</span>
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-black text-[var(--text-primary)] flex items-center gap-2 flex-wrap">
                        {tester.displayName || t('admin.tester_label', 'Tester')}
                        {unreadCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-red-500 text-white text-4xs font-black uppercase tracking-widest">
                            {t('admin.unread_label', 'Ulest {{count}}', { count: unreadCount })}
                          </span>
                        )}
                        {/* Expiry / status badges */}
                        {!status.hasMetRequirements && !status.isAmbassador && status.daysRemaining !== null && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-4xs font-black uppercase tracking-widest ${
                            status.isExpired
                              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                              : status.daysRemaining <= 5
                                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                : 'bg-[var(--bg-input)]/50 text-[var(--text-secondary)] border border-[var(--border-secondary)]'
                          }`}>
                            <Clock size={8} />
                            {status.isExpired
                              ? t('admin.tester_expired', 'Utløpt')
                              : t('admin.tester_days_left', '{{days}}d igjen', { days: status.daysRemaining })}
                          </span>
                        )}
                        {status.isAmbassador && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 text-4xs font-black uppercase tracking-widest">
                            <Award size={8} />
                            {t('admin.tester_ambassador', 'Ambassadør')}
                          </span>
                        )}
                        {status.hasMetRequirements && !status.isAmbassador && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-4xs font-black uppercase tracking-widest">
                            <Shield size={8} />
                            {t('admin.tester_active', 'Aktiv')}
                          </span>
                        )}
                        {tester.platform && tester.platform !== 'web' && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-4xs font-black uppercase tracking-widest ${
                            tester.platform === 'ios'
                              ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                              : 'bg-green-500/20 text-green-400 border border-green-500/30'
                          }`}>
                            <Smartphone size={8} />
                            {tester.platform === 'ios' ? 'iOS' : 'Android'}
                          </span>
                        )}
                        {tester.testflight_opt_in && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-4xs font-black uppercase tracking-widest ${
                            tester.testflight_synced
                              ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                              : tester.testflight_sync_error
                                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                : 'bg-[var(--bg-input)]/50 text-[var(--text-secondary)] border border-[var(--border-secondary)]'
                          }`}>
                            <Smartphone size={8} />
                            {tester.testflight_synced
                              ? 'iOS'
                              : tester.testflight_sync_error
                                ? t('admin.testflight_ios_error', 'iOS feil')
                                : t('admin.testflight_ios_pending', 'iOS venter')}
                          </span>
                        )}
                        {tester.googleplay_opt_in && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-4xs font-black uppercase tracking-widest ${
                            tester.googleplay_synced
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                              : tester.googleplay_sync_error
                                ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                                : 'bg-[var(--bg-input)]/50 text-[var(--text-secondary)] border border-[var(--border-secondary)]'
                          }`}>
                            <Smartphone size={8} />
                            {tester.googleplay_synced
                              ? 'Android'
                              : tester.googleplay_sync_error
                                ? t('admin.googleplay_android_error', 'Android feil')
                                : t('admin.googleplay_android_pending', 'Android venter')}
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-[var(--text-muted)]">
                        {tester.email || tester.uid}
                        {lastMsgMs > 0 && (
                          <span className="ml-2 text-blue-400/70">
                            · {t('admin.last_message_label', 'Sist: {{when}}', { when: new Date(lastMsgMs).toLocaleDateString('no-NO', { day: 'numeric', month: 'short' }) })}
                          </span>
                        )}
                      </p>
                    </div>
                    <ChevronDown size={16} className={`text-[var(--text-muted)] transition-transform shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>
                  {/* Action buttons — stopPropagation so they don't toggle expand */}
                  <div className={`flex gap-2 ${isMobileViewport ? 'w-full' : ''}`} onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => onOpenTesterInbox(tester)}
                      className={`px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 ${isMobileViewport ? 'flex-1' : ''} ${unreadCount > 0
                        ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20'
                        : 'bg-blue-600/20 border border-blue-500/30 text-blue-300 hover:bg-blue-600/30'
                        }`}
                    >
                      <MessageSquare size={11} />
                      {t('admin.open_inbox_btn', 'Chat')}
                      {msgCount > 0 && (
                        <span className={`ml-1 px-1.5 py-0.5 rounded-full text-4xs font-black ${unreadCount > 0 ? 'bg-white/20' : 'bg-blue-500/20'}`}>
                          {msgCount}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => onEndTesterByAdmin(tester)}
                      disabled={processingId === `end-${tester.uid}`}
                      className={`px-4 py-2 rounded-xl bg-red-600/10 border border-red-500/30 text-red-300 text-2xs font-black uppercase tracking-widest hover:bg-red-600/20 disabled:opacity-60 ${isMobileViewport ? 'flex-1' : ''}`}
                    >
                      {t('admin.end_tester_btn', 'Avslutt tester')}
                    </button>
                  </div>
                </div>

                {/* Expanded detail panel */}
                {isExpanded && (
                  <div className="px-4 pb-4 pt-0 border-t border-[var(--border-primary)] space-y-3">
                    {/* Status label */}
                    <div className="flex items-center gap-2 pt-3">
                      {status.isAmbassador ? (
                        <><Award size={14} className="text-amber-400" /><span className="text-2xs font-black text-amber-400 uppercase tracking-widest">{t('admin.tester_status_ambassador', 'Ambassadør-status')}</span></>
                      ) : status.hasMetRequirements ? (
                        <><Shield size={14} className="text-emerald-400" /><span className="text-2xs font-black text-emerald-400 uppercase tracking-widest">{t('admin.tester_status_safe', 'Krav oppfylt')}</span></>
                      ) : (
                        <><AlertTriangle size={14} className="text-[var(--text-secondary)]" /><span className="text-2xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.tester_status_trial', 'Prøveperiode')}</span></>
                      )}
                    </div>

                    {/* Progress bars */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-[var(--bg-input)] rounded-xl p-3 border border-[var(--border-primary)]">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.tester_feedback', 'Tilbakemeldinger')}</span>
                          <span className={`text-2xs font-bold ${status.feedbackCount > 0 ? 'text-emerald-400' : 'text-[var(--text-muted)]'}`}>
                            {status.feedbackCount}
                          </span>
                        </div>
                        <div className="h-1.5 bg-[var(--bg-input)] rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${status.feedbackCount > 0 ? 'bg-emerald-500' : 'bg-[var(--bg-input)]'}`}
                               style={{ width: status.feedbackCount > 0 ? '100%' : '0%' }} />
                        </div>
                      </div>

                      <div className="bg-[var(--bg-input)] rounded-xl p-3 border border-[var(--border-primary)]">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t('admin.tester_shares', 'Delinger')}</span>
                          <span className={`text-2xs font-bold ${status.promoCount >= 5 ? 'text-amber-400' : 'text-[var(--text-muted)]'}`}>
                            {status.promoCount} / 5
                          </span>
                        </div>
                        <div className="h-1.5 bg-[var(--bg-input)] rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${status.promoCount >= 5 ? 'bg-amber-500' : status.promoCount > 0 ? 'bg-amber-500/60' : 'bg-[var(--bg-input)]'}`}
                               style={{ width: `${Math.min(100, (status.promoCount / 5) * 100)}%` }} />
                        </div>
                      </div>
                    </div>

                    {/* Extra info row */}
                    <div className="flex flex-wrap gap-3 text-3xs text-[var(--text-muted)]">
                      {tester.tester_joined_at && (
                        <span>
                          {t('admin.tester_joined', 'Ble tester')}: {new Date(tester.tester_joined_at?.seconds ? tester.tester_joined_at.seconds * 1000 : tester.tester_joined_at).toLocaleDateString()}
                        </span>
                      )}
                      {status.daysRemaining !== null && !status.isAmbassador && !status.hasMetRequirements && (
                        <span className={status.daysRemaining <= 5 ? 'text-red-400' : ''}>
                          {status.isExpired
                            ? t('admin.tester_trial_expired', 'Prøveperioden er utløpt')
                            : t('admin.tester_days_remaining', '{{days}} dager igjen av prøveperioden', { days: status.daysRemaining })}
                        </span>
                      )}
                      {status.isAmbassador && tester.tester_valid_until && (
                        <span className="text-amber-400/80">
                          {t('admin.tester_valid_until', 'Gyldig til {{date}}', {
                            date: new Date(tester.tester_valid_until?.seconds ? tester.tester_valid_until.seconds * 1000 : tester.tester_valid_until).toLocaleDateString()
                          })}
                        </span>
                      )}
                    </div>

                    {/* Campaign emails received */}
                    <div className="pt-2 border-t border-[var(--border-primary)]">
                      <span className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest">
                        {t('admin.tester_campaign_emails', 'Mottatte kampanje-eposter')}
                      </span>
                      {loadingCampaignUid === tester.uid ? (
                        <div className="flex items-center gap-1.5 mt-1">
                          <Loader2 size={10} className="animate-spin text-[var(--text-muted)]" />
                          <span className="text-3xs text-[var(--text-muted)]">Laster...</span>
                        </div>
                      ) : (campaignSentByUid.get(tester.uid) || []).length === 0 ? (
                        <p className="text-3xs text-[var(--text-muted)] mt-1">{t('admin.tester_no_campaign_emails', 'Ingen kampanje-eposter sendt')}</p>
                      ) : (
                        <div className="mt-1 space-y-0.5">
                          {(campaignSentByUid.get(tester.uid) || []).map(entry => (
                            <div key={entry.id} className="flex items-center justify-between text-3xs text-[var(--text-secondary)] py-0.5">
                              <span className="truncate mr-2">
                                {templateNames.get(entry.template_id) || entry.template_id}
                                {entry.step_number != null && <span className="text-[var(--text-muted)]"> (steg {entry.step_number})</span>}
                                {entry.broadcast && <span className="text-blue-400/60"> broadcast</span>}
                              </span>
                              <span className="text-[var(--text-muted)] shrink-0">
                                {entry.sent_at?.seconds
                                  ? new Date(entry.sent_at.seconds * 1000).toLocaleString('no-NO', { day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                  : '—'}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pending promotion proofs — ambassador program */}
      <div className="space-y-3 mt-6">
          <div className="flex items-center justify-between">
            <h3 className="text-2xs font-black uppercase tracking-widest text-amber-400">
              <Award size={11} className="inline mr-1.5 -mt-0.5" />
              Delingsbevis ({promotions.length})
            </h3>
            <button
              onClick={loadPromotions}
              disabled={promosLoading}
              className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-amber-400 hover:bg-amber-500/10 transition-all disabled:opacity-40"
              title="Last inn på nytt"
            >
              <RefreshCw size={12} className={promosLoading ? 'animate-spin' : ''} />
            </button>
          </div>
          {promosLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={16} className="animate-spin text-[var(--text-muted)]" />
            </div>
          ) : promotions.length === 0 ? (
            <p className="text-tiny text-[var(--text-muted)] text-center py-6">Ingen ventende delingsbevis</p>
          ) : (
            promotions.map(promo => (
              <div key={promo.id} className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-black text-[var(--text-primary)]">{promo.testerName}</p>
                    <p className="text-2xs text-[var(--text-muted)]">{promo.testerEmail}</p>
                  </div>
                  {promo.createdAt && (
                    <span className="text-3xs font-mono text-[var(--text-muted)] shrink-0">
                      {new Date(promo.createdAt).toLocaleDateString()}
                    </span>
                  )}
                </div>

                {promo.url && (
                  <a
                    href={promo.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-xs text-blue-400 hover:text-blue-300 truncate"
                  >
                    <ExternalLink size={12} className="shrink-0" />
                    {promo.url}
                  </a>
                )}

                {promo.imageUrl && (
                  <a href={promo.imageUrl} target="_blank" rel="noopener noreferrer">
                    <img src={promo.imageUrl} alt="Skjermbilde" className="w-full max-h-40 object-cover rounded-lg border border-[var(--border-secondary)]" />
                  </a>
                )}

                {promo.description && (
                  <p className="text-tiny text-[var(--text-secondary)] italic">"{promo.description}"</p>
                )}

                {rejectingPromoId === promo.id ? (
                  <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl space-y-3">
                    <div className="flex items-center gap-2 text-red-400">
                      <AlertTriangle size={14} />
                      <span className="text-2xs font-black uppercase tracking-widest">{t('admin.reject_promo_title', 'Begrunnelse for avvisning')}</span>
                    </div>
                    <textarea
                      value={promoRejectReason}
                      onChange={e => setPromoRejectReason(e.target.value)}
                      placeholder={t('admin.reject_promo_placeholder', 'Valgfritt: F.eks. lenken er ikke offentlig, skjermbildet viser ikke innlegget...')}
                      className="w-full bg-[var(--bg-input)] border border-[var(--border-secondary)] rounded-lg p-3 text-xs text-[var(--text-secondary)] placeholder:text-[var(--text-muted)] resize-none focus:outline-none focus:border-red-500/40"
                      rows={2}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handlePromoAction(promo.id, 'rejected', promoRejectReason)}
                        disabled={promoProcessingId === promo.id}
                        className="flex-1 bg-red-600/20 hover:bg-red-600 border border-red-500/30 text-red-400 hover:text-white py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50"
                      >
                        {promoProcessingId === promo.id ? <Loader2 className="animate-spin" size={12} /> : <X size={12} />}
                        {t('admin.confirm_reject', 'Bekreft avslag')}
                      </button>
                      <button
                        onClick={() => { setRejectingPromoId(null); setPromoRejectReason(''); }}
                        className="px-4 py-2.5 rounded-xl bg-[var(--bg-input)] text-[var(--text-secondary)] text-2xs font-black uppercase tracking-widest hover:bg-[var(--bg-input)] transition-all"
                      >
                        {t('common.cancel', 'Avbryt')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => handlePromoAction(promo.id, 'approved')}
                      disabled={promoProcessingId === promo.id}
                      className="flex-1 bg-emerald-600/10 hover:bg-emerald-600 border border-emerald-500/20 text-emerald-500 hover:text-white py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50"
                    >
                      {promoProcessingId === promo.id ? <Loader2 className="animate-spin" size={12} /> : <CheckCircle size={12} />}
                      {t('admin.approve_btn', 'Godkjenn')}
                    </button>
                    <button
                      onClick={() => setRejectingPromoId(promo.id)}
                      disabled={promoProcessingId === promo.id}
                      className="flex-1 bg-[var(--bg-input)] hover:bg-red-900/30 text-[var(--text-secondary)] hover:text-red-400 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50"
                    >
                      <X size={12} />
                      {t('admin.reject_btn', 'Avvis')}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
      ) : (
        <div className="space-y-4">
          {/* Search + Paid filter + Country filter pills */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  type="text"
                  value={userSearch}
                  onChange={e => setUserSearch(e.target.value)}
                  placeholder={t('admin.user_search_placeholder', 'Søk på navn eller e-post...')}
                  className="w-full bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-xl pl-8 pr-3 py-2.5 text-xs text-[var(--text-secondary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:border-violet-500/40"
                />
              </div>
              <button
                onClick={() => setShowPaidOnly(prev => !prev)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all ${showPaidOnly ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/20' : 'bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-secondary)] hover:border-amber-500/40'}`}
              >
                <CreditCard size={11} />
                {paidCount}
              </button>
            </div>

            {/* Hurtigfilter for land-etterlysninger */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 no-scrollbar">
              <span className="text-4xs font-black uppercase tracking-widest text-[var(--text-muted)] shrink-0 flex items-center gap-1 mr-1">
                <Globe size={11} /> Filter:
              </span>
              <button
                onClick={() => setCountryFilter('all')}
                className={`px-2.5 py-1 rounded-lg text-4xs font-black uppercase tracking-widest transition-all shrink-0 ${countryFilter === 'all' ? 'bg-violet-600 text-white shadow-sm' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'}`}
              >
                {t('common.all', 'Alle')} ({nonTesterUsers.length})
              </button>
              <button
                onClick={() => setCountryFilter('requested_any')}
                className={`px-2.5 py-1 rounded-lg text-4xs font-black uppercase tracking-widest transition-all shrink-0 flex items-center gap-1 ${countryFilter === 'requested_any' ? 'bg-amber-600 text-white shadow-sm' : 'bg-[var(--bg-surface-alt)] text-amber-400 hover:bg-[var(--bg-surface)]'}`}
              >
                <Flag size={10} /> Ønsker land ({usersWithCountryRequestsCount})
              </button>
              <button
                onClick={() => setCountryFilter('sweden')}
                className={`px-2.5 py-1 rounded-lg text-4xs font-black uppercase tracking-widest transition-all shrink-0 flex items-center gap-1 ${countryFilter === 'sweden' ? 'bg-blue-600 text-white shadow-sm' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'}`}
              >
                🇸🇪 Sverige ({swedenCount})
              </button>
              <button
                onClick={() => setCountryFilter('finland')}
                className={`px-2.5 py-1 rounded-lg text-4xs font-black uppercase tracking-widest transition-all shrink-0 flex items-center gap-1 ${countryFilter === 'finland' ? 'bg-blue-700 text-white shadow-sm' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'}`}
              >
                🇫🇮 Finland ({finlandCount})
              </button>
              <button
                onClick={() => setCountryFilter('denmark')}
                className={`px-2.5 py-1 rounded-lg text-4xs font-black uppercase tracking-widest transition-all shrink-0 flex items-center gap-1 ${countryFilter === 'denmark' ? 'bg-red-600 text-white shadow-sm' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'}`}
              >
                🇩🇰 Danmark ({denmarkCount})
              </button>
              <button
                onClick={() => setCountryFilter('iceland')}
                className={`px-2.5 py-1 rounded-lg text-4xs font-black uppercase tracking-widest transition-all shrink-0 flex items-center gap-1 ${countryFilter === 'iceland' ? 'bg-blue-500 text-white shadow-sm' : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] hover:bg-[var(--bg-surface)]'}`}
              >
                🇮🇸 Island ({icelandCount})
              </button>
            </div>
          </div>

          {/* User list */}
          {sortedUsers.length === 0 ? (
            <p className="text-tiny text-[var(--text-muted)] text-center py-8">
              {userSearch.trim() || countryFilter !== 'all' ? t('admin.no_users_found', 'Ingen brukere funnet for dette filteret') : t('admin.no_users_yet', 'Ingen brukere ennå')}
            </p>
          ) : (
            <div className="space-y-2">
              {sortedUsers.map(user => {
                const thread = testerThreadByUid.get(user.uid);
                const unread = thread?.unreadForAdmin || 0;
                const msgCount = thread?.messageCount || 0;
                const lastMsgMs = threadSortValue(thread?.lastMessageAt);
                const isPaid = user.subscriptionStatus === 'paid';
                const requestedCountriesList = getCountryRequestsForUser(user, thread);

                return (
                  <div key={user.uid} className={`border rounded-2xl p-4 flex flex-col gap-3 ${requestedCountriesList.length > 0 ? 'bg-amber-500/5 border-amber-500/30' : isPaid ? 'bg-amber-500/5 border-amber-500/25' : unread > 0 ? 'bg-red-500/5 border-red-500/25' : 'bg-[var(--bg-surface-alt)] border-[var(--border-secondary)]'}`}>
                    <div className="flex items-center gap-3 w-full">
                      <div className="relative shrink-0">
                        <div className={`w-9 h-9 rounded-full bg-[var(--bg-input)] overflow-hidden flex items-center justify-center text-[var(--text-muted)] ${isPaid ? 'ring-2 ring-amber-400 border-0' : 'border border-[var(--border-secondary)]'}`}>
                          {user.photoURL ? (
                            <img src={user.photoURL} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <Users size={16} />
                          )}
                        </div>
                        {unread > 0 && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 border-2 border-[#0a0c0c] flex items-center justify-center">
                            <span className="text-4xs font-black text-white">{unread}</span>
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-black text-[var(--text-primary)] truncate">{user.displayName || user.email || user.uid}</p>

                          {/* Country Request Badges */}
                          {requestedCountriesList.map(c => (
                            <span key={c.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 text-4xs font-black uppercase tracking-widest shrink-0 animate-in fade-in">
                              <span>{c.flag}</span> Ønsker {c.name}
                            </span>
                          ))}

                          {isPaid && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30 text-4xs font-black uppercase tracking-widest shrink-0">
                              <CreditCard size={8} />{user.subscriptionProvider === 'vipps' ? 'Vipps' : user.subscriptionProvider === 'apple' ? 'Apple' : 'Stripe'}
                            </span>
                          )}
                          {unread > 0 && (
                            <span className="px-2 py-0.5 rounded-full bg-red-500 text-white text-4xs font-black uppercase tracking-widest shrink-0">
                              {t('admin.unread_label', 'Ulest {{count}}', { count: unread })}
                            </span>
                          )}
                          {user.testflight_synced && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 border border-cyan-500/30 text-4xs font-black uppercase tracking-widest shrink-0">
                              <Smartphone size={8} />iOS
                            </span>
                          )}
                          {user.googleplay_synced && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-4xs font-black uppercase tracking-widest shrink-0">
                              <Smartphone size={8} />Android
                            </span>
                          )}
                        </div>
                        <p className="text-2xs text-[var(--text-muted)] truncate">
                          {user.email}{isPaid && user.platform ? ` · ${user.platform}` : ''}
                          {lastMsgMs > 0 && (
                            <span className="ml-2 text-blue-400/70">
                              · {t('admin.last_message_label', 'Sist: {{when}}', { when: new Date(lastMsgMs).toLocaleDateString('no-NO', { day: 'numeric', month: 'short' }) })}
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {onPromoteToTester && !user.isTester && (
                          <button
                            onClick={() => {
                              setPromotingUserId(user.uid);
                              setPromoteDays(90);
                              setCustomWelcomeMsg(buildDefaultWelcomeMessage(user.displayName || 'bruker', requestedCountriesList));
                            }}
                            className="px-3 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-600/30 flex items-center gap-1"
                          >
                            <UserCheck size={11} />
                            {t('admin.promote_to_tester', 'Tester')}
                          </button>
                        )}
                        <button
                          onClick={() => onOpenUserInbox(user)}
                          className={`px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest transition-all flex items-center gap-1.5 ${unread > 0 ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/20' : 'bg-blue-600/20 border border-blue-500/30 text-blue-300 hover:bg-blue-600/30'}`}
                        >
                          <MessageSquare size={11} />
                          {t('admin.open_inbox_btn', 'Chat')}
                          {msgCount > 0 && (
                            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-4xs font-black ${unread > 0 ? 'bg-white/20' : 'bg-blue-500/20'}`}>
                              {msgCount}
                            </span>
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Modal/Skjema for å godkjenne som tester med velkomstmelding */}
                    {promotingUserId === user.uid && (
                      <div className="mt-1 pt-3 border-t border-[var(--border-secondary)] space-y-3 w-full animate-in fade-in slide-in-from-top-2 duration-200">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-bold text-[var(--text-primary)]">
                            {t('admin.select_tester_duration', 'Velg varighet for tester-tilgang (dager)')}
                          </p>
                          {requestedCountriesList.length > 0 && (
                            <span className="text-3xs text-amber-400 font-bold">
                              Etterlyst: {requestedCountriesList.map(c => `${c.flag} ${c.name}`).join(', ')}
                            </span>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {[30, 90, 180, 365].map(days => (
                            <button
                              key={days}
                              onClick={() => setPromoteDays(days)}
                              className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${promoteDays === days ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/20' : 'bg-[var(--bg-input)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-secondary)]'}`}
                            >
                              {days} {t('admin.days_label', 'dager')}{days === 90 ? ` (${t('admin.default_label', 'Standard')})` : ''}
                            </button>
                          ))}
                        </div>

                        <div className="flex items-center gap-2 pt-1">
                          <span className="text-xs text-[var(--text-muted)]">{t('admin.custom_days', 'Eller egendefinert:')}</span>
                          <input
                            type="number"
                            min="1"
                            max="3650"
                            value={promoteDays}
                            onChange={(e) => setPromoteDays(Math.max(1, parseInt(e.target.value) || 1))}
                            className="w-24 px-3 py-1.5 rounded-xl bg-[var(--bg-input)] border border-[var(--border-secondary)] text-xs text-[var(--text-primary)] focus:outline-none focus:border-emerald-500"
                          />
                          <span className="text-xs text-[var(--text-muted)]">{t('admin.days_label', 'dager')}</span>
                        </div>

                        {/* Velkomstmelding og Chat opsjon */}
                        <div className="bg-[var(--bg-input)] border border-[var(--border-secondary)] p-3 rounded-xl space-y-2">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={sendWelcomeMsg}
                              onChange={e => setSendWelcomeMsg(e.target.checked)}
                              className="rounded border-[var(--border-secondary)] bg-[var(--bg-surface)] text-emerald-500 focus:ring-emerald-500"
                            />
                            <span className="text-xs font-bold text-[var(--text-primary)] flex items-center gap-1.5">
                              <MessageSquare size={13} className="text-emerald-400" />
                              Send tilpasset velkomstmelding i chatten
                            </span>
                          </label>

                          {sendWelcomeMsg && (
                            <textarea
                              rows={4}
                              value={customWelcomeMsg}
                              onChange={e => setCustomWelcomeMsg(e.target.value)}
                              placeholder="Skriv velkomstmelding..."
                              className="w-full bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-lg p-2.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-emerald-500/50 resize-none"
                            />
                          )}
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                          <button
                            onClick={() => { setPromotingUserId(null); setPromoteDays(90); }}
                            className="px-4 py-2 rounded-xl text-2xs font-black uppercase tracking-widest bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all"
                          >
                            {t('common.cancel', 'Avbryt')}
                          </button>
                          <button
                            onClick={async () => {
                              if (onPromoteToTester) {
                                onPromoteToTester(user, promoteDays);
                              }
                              if (sendWelcomeMsg && currentUser && customWelcomeMsg.trim()) {
                                try {
                                  await sendTesterMessage(user.uid, currentUser, 'admin', customWelcomeMsg.trim(), []);
                                  onOpenUserInbox(user);
                                } catch (err) {
                                  console.warn('Feil ved sending av velkomstmelding:', err);
                                }
                              }
                              setPromotingUserId(null);
                              setPromoteDays(90);
                            }}
                            className="px-4 py-2.5 rounded-xl text-2xs font-black uppercase tracking-widest bg-emerald-600 text-white shadow-lg shadow-emerald-500/20 hover:bg-emerald-500 transition-all flex items-center gap-1.5"
                          >
                            <UserCheck size={13} />
                            {sendWelcomeMsg ? 'Godkjenn & Send melding' : t('admin.confirm_promote', 'Godkjenn som tester')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminTestersPanel;
